const initHeroCarousel = (carousel) => {
	if (carousel.dataset.heroCarouselReady === 'true') {
		return;
	}

	carousel.dataset.heroCarouselReady = 'true';

	const slides = Array.from(carousel.querySelectorAll('[data-hero-slide]'));
	const dots = Array.from(carousel.querySelectorAll('[data-hero-dot]'));
	const previousButton = carousel.querySelector('[data-hero-prev]');
	const nextButton = carousel.querySelector('[data-hero-next]');
	const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	const shouldAutoplay = carousel.dataset.autoplay === 'true' && !prefersReducedMotion;
	const interval = Number.parseInt(carousel.dataset.interval || '6000', 10);
	let activeIndex = 0;
	let timer;
	let transitionTimer;

	if (slides.length < 2) {
		return;
	}

	slides.forEach((slide) => {
		const video = slide.querySelector('video');

		if (video) {
			video.preload = 'auto';
			video.load();
		}
	});

	const setActiveSlide = (index) => {
		const nextIndex = (index + slides.length) % slides.length;

		if (nextIndex === activeIndex) {
			return;
		}

		window.clearTimeout(transitionTimer);

		slides[activeIndex].classList.remove('is-active', 'is-entering');
		slides[activeIndex].classList.add('is-leaving');
		slides[activeIndex].setAttribute('aria-hidden', 'true');

		slides[nextIndex].classList.remove('is-leaving');
		slides[nextIndex].classList.add('is-entering', 'is-active');
		slides[nextIndex].setAttribute('aria-hidden', 'false');

		transitionTimer = window.setTimeout(() => {
			slides.forEach((slide, slideIndex) => {
				if (slideIndex !== nextIndex) {
					slide.classList.remove('is-leaving', 'is-entering', 'is-active');
				}
			});
		}, 900);

		activeIndex = nextIndex;

		dots.forEach((dot, dotIndex) => {
			const isActive = dotIndex === activeIndex;

			dot.classList.toggle('is-active', isActive);
			dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
		});
	};

	const stopAutoplay = () => window.clearInterval(timer);
	const startAutoplay = () => {
		if (!shouldAutoplay) {
			return;
		}

		stopAutoplay();
		timer = window.setInterval(() => setActiveSlide(activeIndex + 1), interval);
	};

	previousButton?.addEventListener('click', () => {
		setActiveSlide(activeIndex - 1);
		startAutoplay();
	});

	nextButton?.addEventListener('click', () => {
		setActiveSlide(activeIndex + 1);
		startAutoplay();
	});

	dots.forEach((dot) => {
		dot.addEventListener('click', () => {
			setActiveSlide(Number.parseInt(dot.dataset.heroDot, 10));
			startAutoplay();
		});
	});

	carousel.addEventListener('mouseenter', stopAutoplay);
	carousel.addEventListener('mouseleave', startAutoplay);
	carousel.addEventListener('focusin', stopAutoplay);
	carousel.addEventListener('focusout', startAutoplay);

	startAutoplay();
};

const initHomepageAccordion = (scope) => {
	if (scope.dataset.homepageAccordionReady === 'true') {
		return;
	}

	scope.dataset.homepageAccordionReady = 'true';

	const items = Array.from(scope.querySelectorAll('[data-homepage-accordion-item]'));

	const setItemState = (item, isOpen) => {
		const toggle = item.querySelector('[data-homepage-accordion-toggle]');
		const panel = item.querySelector('[data-homepage-accordion-panel]');

		item.classList.toggle('homepage-topic--active', isOpen);
		toggle?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
		panel?.classList.toggle('is-open', isOpen);
	};

	items.forEach((item) => {
		const toggle = item.querySelector('[data-homepage-accordion-toggle]');
		const panel = item.querySelector('[data-homepage-accordion-panel]');

		if (!toggle || !panel) {
			return;
		}

		setItemState(item, toggle.getAttribute('aria-expanded') === 'true');

		toggle.addEventListener('click', () => {
			const willOpen = toggle.getAttribute('aria-expanded') !== 'true';

			items.forEach((otherItem) => {
				setItemState(otherItem, otherItem === item && willOpen);
			});
		});
	});
};

const initMilestonesScroller = (section) => {
	const viewport = section.querySelector('[data-milestones-viewport]');
	const timeline = section.querySelector('.homepage-milestones__timeline');
	const previousButton = section.querySelector('[data-milestones-prev]');
	const nextButton = section.querySelector('[data-milestones-next]');

	if (!viewport || !timeline || !previousButton || !nextButton) {
		return;
	}

	const updateProgress = () => {
		const firstNode = timeline.querySelector('.homepage-milestone__node');

		if (firstNode) {
			const timelineRect = timeline.getBoundingClientRect();
			const nodeRect = firstNode.getBoundingClientRect();
			const nodeCenterY = nodeRect.top + (nodeRect.height / 2) - timelineRect.top;
			const trackHeight = parseFloat(window.getComputedStyle(timeline).getPropertyValue('--timeline-stroke')) || 6;
			const trackTop = nodeCenterY - (trackHeight / 2);

			timeline.style.setProperty('--timeline-track-top', `${trackTop.toFixed(1)}px`);
		}

		const currentPoint = viewport.scrollLeft + (viewport.clientWidth * 0.34);
		const fillX = Math.min(Math.max(currentPoint, 0), timeline.scrollWidth);

		timeline.style.setProperty('--timeline-progress-x', `${fillX.toFixed(1)}px`);

		timeline.querySelectorAll('.homepage-milestone').forEach((milestone) => {
			const node = milestone.querySelector('.homepage-milestone__node');

			if (!node) {
				return;
			}

			const nodeX = node.getBoundingClientRect().left + (node.offsetWidth / 2) - timeline.getBoundingClientRect().left;
			milestone.classList.toggle('homepage-milestone--passed', fillX >= nodeX);
		});
	};

	const easeInOutCubic = (progress) => progress < 0.5
		? 4 * progress * progress * progress
		: 1 - Math.pow(-2 * progress + 2, 3) / 2;

	const glideBy = (distance) => {
		const start = viewport.scrollLeft;
		const maxScroll = viewport.scrollWidth - viewport.clientWidth;
		const target = Math.min(Math.max(start + distance, 0), maxScroll);
		const duration = 1100;
		const startTime = window.performance.now();

		const tick = (now) => {
			const elapsed = Math.min((now - startTime) / duration, 1);

			viewport.scrollLeft = start + (target - start) * easeInOutCubic(elapsed);
			updateProgress();

			if (elapsed < 1) {
				window.requestAnimationFrame(tick);
			}
		};

		window.requestAnimationFrame(tick);
	};

	const getStep = () => Math.max(viewport.clientWidth * 0.42, 260);

	previousButton.addEventListener('click', () => {
		glideBy(-getStep());
	});

	nextButton.addEventListener('click', () => {
		glideBy(getStep());
	});

	viewport.addEventListener('scroll', updateProgress, { passive: true });
	window.addEventListener('resize', updateProgress);
	window.addEventListener('load', updateProgress, { once: true });
	updateProgress();
};

const initHistoryVideo = (wrapper) => {
	const video = wrapper.querySelector('[data-history-video]');
	const playButton = wrapper.querySelector('[data-history-video-play]');
	const controlPlayButton = wrapper.querySelector('[data-history-control-play]');
	const scrubber = wrapper.querySelector('[data-history-scrubber]');
	const timeLabel = wrapper.querySelector('[data-history-time]');
	const fullscreenButton = wrapper.querySelector('[data-history-fullscreen]');

	if (!video || !playButton) {
		return;
	}

	const formatTime = (seconds) => {
		if (!Number.isFinite(seconds)) {
			return '0:00';
		}

		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = Math.floor(seconds % 60).toString().padStart(2, '0');

		return `${minutes}:${remainingSeconds}`;
	};

	const updateProgress = () => {
		const duration = video.duration || 0;
		const progress = duration ? Math.min(Math.max((video.currentTime / duration) * 100, 0), 100) : 0;

		if (scrubber) {
			scrubber.value = progress.toFixed(1);
			scrubber.style.setProperty('--history-progress', `${progress}%`);
		}

		if (timeLabel) {
			timeLabel.textContent = `${formatTime(video.currentTime)} / ${formatTime(duration)}`;
		}
	};

	const setPlayingState = () => {
		const isPlaying = !video.paused && !video.ended;

		wrapper.classList.toggle('is-playing', isPlaying);
		playButton.setAttribute('aria-label', isPlaying ? 'Pauzeer history video' : 'Speel history video af');
		controlPlayButton?.setAttribute('aria-label', isPlaying ? 'Pauzeer video' : 'Speel video af');
		updateProgress();
	};

	const playVideo = (scrollToVideo = false) => {
		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		if (scrollToVideo) {
			wrapper.scrollIntoView({
				behavior: prefersReducedMotion ? 'auto' : 'smooth',
				block: 'center',
			});
		}

		const playPromise = video.play();

		if (playPromise && typeof playPromise.catch === 'function') {
			playPromise.catch(() => {});
		}
	};

	const togglePlayback = () => {
		if (video.paused || video.ended) {
			playVideo();
			return;
		}

		video.pause();
	};

	playButton.addEventListener('click', () => {
		playVideo(true);
	});

	controlPlayButton?.addEventListener('click', togglePlayback);

	scrubber?.addEventListener('input', () => {
		if (!video.duration) {
			return;
		}

		const nextTime = video.duration * (Number.parseFloat(scrubber.value) / 100);
		video.currentTime = nextTime;
		updateProgress();
	});

	fullscreenButton?.addEventListener('click', () => {
		if (document.fullscreenElement || document.webkitFullscreenElement) {
			if (document.exitFullscreen) {
				document.exitFullscreen();
			} else if (document.webkitExitFullscreen) {
				document.webkitExitFullscreen();
			}

			return;
		}

		if (wrapper.requestFullscreen) {
			wrapper.requestFullscreen();
			return;
		}

		if (wrapper.webkitRequestFullscreen) {
			wrapper.webkitRequestFullscreen();
			return;
		}

		if (video.webkitEnterFullscreen) {
			video.webkitEnterFullscreen();
		}
	});

	video.addEventListener('play', setPlayingState);
	video.addEventListener('pause', setPlayingState);
	video.addEventListener('ended', setPlayingState);
	video.addEventListener('loadedmetadata', updateProgress);
	video.addEventListener('durationchange', updateProgress);
	video.addEventListener('timeupdate', updateProgress);
	fullscreenButton?.addEventListener('click', updateProgress);
	updateProgress();
	setPlayingState();
};

const initProductPhotoCarousel = (viewport) => {
	if (viewport.dataset.productPhotoCarouselReady === 'true') {
		return;
	}

	viewport.dataset.productPhotoCarouselReady = 'true';

	const section = viewport.closest('.rf-product-photo-carousel');
	const track = viewport.querySelector('.rf-product-photo-carousel__track');
	const previousButton = section?.querySelector('[data-product-photo-prev]');
	const nextButton = section?.querySelector('[data-product-photo-next]');

	if (!track) {
		return;
	}

	const originalItems = Array.from(track.children).map((item) => item.cloneNode(true));

	if (!originalItems.length) {
		return;
	}

	let itemStep = 0;
	let itemCount = 0;
	let currentIndex = 0;

	const getItemsPerView = () => {
		if (window.matchMedia('(min-width: 1200px)').matches) {
			return 4;
		}

		if (window.matchMedia('(min-width: 768px)').matches) {
			return 2;
		}

		return 1;
	};

	const measure = () => {
		const firstItem = track.querySelector('.rf-product-photo-carousel__item');
		const trackStyle = window.getComputedStyle(track);
		const gap = Number.parseFloat(trackStyle.columnGap || trackStyle.gap || '0') || 0;

		itemStep = firstItem ? firstItem.getBoundingClientRect().width + gap : 0;
	};

	const setPosition = (animate = true) => {
		track.classList.toggle('is-resetting', !animate);
		track.style.transform = `translate3d(${-currentIndex * itemStep}px, 0, 0)`;

		if (!animate) {
			track.offsetHeight;
			requestAnimationFrame(() => track.classList.remove('is-resetting'));
		}
	};

	const build = () => {
		const perView = getItemsPerView();
		const usableItems = [];
		const minimumItems = Math.max(originalItems.length, perView + 1);

		while (usableItems.length < minimumItems) {
			usableItems.push(originalItems[usableItems.length % originalItems.length].cloneNode(true));
		}

		const beforeItems = usableItems.slice(-perView).map((item) => item.cloneNode(true));
		const afterItems = usableItems.slice(0, perView).map((item) => item.cloneNode(true));

		track.replaceChildren(...beforeItems, ...usableItems, ...afterItems);
		itemCount = usableItems.length;
		currentIndex = perView;
		measure();
		setPosition(false);
		window.dispatchEvent(new CustomEvent('productPhotoCarouselRebuilt'));
	};

	const move = (direction) => {
		if (!itemStep || itemCount <= 1) {
			return;
		}

		currentIndex += direction;
		setPosition(true);
	};

	track.addEventListener('transitionend', () => {
		const perView = getItemsPerView();

		if (currentIndex >= itemCount + perView) {
			currentIndex = perView;
			setPosition(false);
		}

		if (currentIndex < perView) {
			currentIndex = itemCount + perView - 1;
			setPosition(false);
		}
	});

	previousButton?.addEventListener('click', () => move(-1));
	nextButton?.addEventListener('click', () => move(1));
	window.addEventListener('resize', build);
	build();
};

document.querySelectorAll('[data-hero-carousel]').forEach(initHeroCarousel);
document.querySelectorAll('.homepage-manufacture').forEach(initHomepageAccordion);
document.querySelectorAll('.homepage-milestones').forEach(initMilestonesScroller);
document.querySelectorAll('.homepage-history-video').forEach(initHistoryVideo);
document.querySelectorAll('[data-product-photo-carousel]').forEach(initProductPhotoCarousel);
